import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs/operators';
import { resolveModuleIdForUrl } from '../permissions/module-write-scopes';
import {
  MODULE_FLAG_META,
  isActiveModuleReadOnly,
  isReadOnlyModule,
} from '../permissions/read-only-flags';

/**
 * Observable wrapper over the read-only flags, for templates and the click-guard's dimming pass.
 *
 * The actual lookups live in permissions/read-only-flags.ts as plain functions with no DI — the HTTP
 * interceptor calls those directly so that constructing an interceptor never drags this service (and
 * its Router/HttpClient-adjacent dependencies) into the HTTP construction path.
 *
 * A user assigned the Viewer role in a module holds `<Module>User` (so every existing visibility
 * check still passes) plus `<Module>Viewer`. Read-only is per module: someone can be a Viewer in
 * Finance while still writing in Corporate.
 */
@Injectable({ providedIn: 'root' })
export class ReadOnlyService {
  private readonly router = inject(Router);

  // Bumped when the cached permission flags change (sign-in, company switch, a self-managed access
  // edit) so `readOnly$` re-evaluates instead of holding a stale answer.
  private readonly invalidated$ = new BehaviorSubject<void>(undefined);

  /** Read-only state of the module the user is currently in. Re-evaluated on navigation. */
  readonly readOnly$: Observable<boolean> = merge(
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)),
    this.invalidated$
  ).pipe(
    startWith(null),
    map(() => isActiveModuleReadOnly()),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  /**
   * Read-only state of one module, re-evaluated when `readOnly$` is. For UI that belongs to a module
   * other than the one open — the header's Notes button is Corporate's on every page.
   */
  readOnlyIn$(moduleId: number): Observable<boolean> {
    return merge(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)),
      this.invalidated$
    ).pipe(
      startWith(null),
      map(() => isReadOnlyModule(moduleId)),
      distinctUntilChanged()
    );
  }

  /** True when the user holds the Viewer (read-only) role in this module. */
  isReadOnly(moduleId: number): boolean {
    return isReadOnlyModule(moduleId);
  }

  /** True when a request to `url` targets a module the current user can only read. */
  isReadOnlyForUrl(url: string): boolean {
    const moduleId = resolveModuleIdForUrl(url);
    return moduleId !== null && isReadOnlyModule(moduleId);
  }

  /** Read-only state of the module currently open in this tab. */
  isActiveModuleReadOnly(): boolean {
    return isActiveModuleReadOnly();
  }

  /** Every module this user can only read. */
  readOnlyModuleIds(): number[] {
    return Object.keys(MODULE_FLAG_META).map(Number).filter(id => isReadOnlyModule(id));
  }

  /** Call after the permission caches are rewritten so `readOnly$` re-emits. */
  invalidate(): void {
    this.invalidated$.next();
  }
}
